import { writable } from 'svelte/store';
import { getContext, onMount } from 'svelte';
import { FPJS_CONTEXT } from './symbols';
/**
 * API for fetching visitorData.
 *
 * @example ```svelte
 *     <script>
 *       import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-svelte';
 *
 *       const { data, getData, isLoading, error } = useVisitorData(
 *          { extendedResult: true }
 *       );
 *
 *       // Fetch data on mount and ignore cache
 *       // const { data, getData, isLoading, error } = useVisitorData({ extendedResult: true, ignoreCache: true }, { immediate: true })
 *       </script>
 * ```
 * */
export function useVisitorData({ ignoreCache: defaultIgnoreCache, ...options }, { immediate = true } = {}) {
    const dataValue = writable(undefined);
    const loadingValue = writable(false);
    const errorValue = writable(undefined);
    const context = getContext(FPJS_CONTEXT);
    const getData = async (getDataOptions) => {
        loadingValue.set(true);
        const ignoreCache = typeof getDataOptions?.ignoreCache === 'boolean' ? getDataOptions.ignoreCache : defaultIgnoreCache;
        try {
            const result = await context.getVisitorData(options, ignoreCache);
            dataValue.set(result);
            errorValue.set(undefined);
            return result;
        }
        catch (error) {
            dataValue.set(undefined);
            if (error instanceof Error) {
                error.message = `${error.name}: ${error.message}`;
                error.name = 'FPJSAgentError';
                errorValue.set(error);
            }
            return undefined;
        }
        finally {
            loadingValue.set(false);
        }
    };
    onMount(async () => {
        if (immediate) {
            await getData();
        }
    });
    return {
        data: dataValue,
        error: errorValue,
        isLoading: loadingValue,
        getData,
    };
}
